import { useQuery, useMutation, useQueryClient, useInfiniteQuery, UseQueryOptions, UseMutationOptions, UseInfiniteQueryOptions } from '@tanstack/react-query';
import type {
  FullProfile,
  ProfileSummary,
  SwipeRequest,
  SwipeResponse,
  Match,
  Message,
  BootstrapResponse,
  PaginatedResponse,
  OnboardingData,
  NotificationCounters,
  SubscriptionStatusResponse,
  MyLikesResponse,
  RemoveLikeRequest,
  BasicFilters,
  BasicFiltersResponse,
  MessageCreate,
  MessageUpdate,
  MessageReport,
} from '@/types';
import { api } from '@/api';
import { useAppStore } from '@/store';
import { TelegramUtils } from '@/utils/telegram';
import { mapCardToProfileSummary, mapMatchResponseToMatch, mapMessageResponseToMessage } from '@/utils/mappers';

// Query Keys
export const queryKeys = {
  bootstrap: ['bootstrap'] as const,
  profile: (id: string) => ['profile', id] as const,
  myProfile: ['profile', 'me'] as const,
  feedProfiles: ['feed', 'profiles'] as const,
  matches: ['matches'] as const,
  match: (id: string) => ['match', id] as const,
  messages: (matchId: string) => ['messages', matchId] as const,
  likes: ['likes'] as const,
  notificationCounters: ['notification-counters'] as const,
} as const;

// Bootstrap App
export function useBootstrap(options?: UseQueryOptions<BootstrapResponse>) {
  const { 
    setUser, 
    setAuthenticated, 
    setTelegramUser, 
    setTelegramReady,
    setOnboardingCompleted,
    setOnboardingStep,
    updateOnboardingData,
    setActiveScreen,
  } = useAppStore();

  return useQuery({
    queryKey: queryKeys.bootstrap,
    queryFn: async () => {
      const initData = TelegramUtils.getInitData();
      console.log('🚀 Starting bootstrap...');
      
      const response = await api.bootstrap(initData);
      console.log('📦 Bootstrap response received');
      
      // Update store with user data (can be null for new users)
      if (response.me) {
        setUser(response.me);
        setAuthenticated(true);
        
        const { gender, status } = response.me;
        
        const isIT = response.me.is_it;
        const lookingFor = response.me.looking_for;
        
        console.log('📊 Bootstrap user:', {
          id: response.me.id,
          gender,
          status,
          is_it: isIT,
          looking_for: lookingFor,
        });
        
        // Set gender in onboarding data if available
        if (gender) {
          updateOnboardingData({ gender } as any);
        }
        
        // Route based on gender, is_it, and status
        if (!gender || gender === null) {
          // No gender -> show gender select
          console.log('→ No gender, showing gender select');
          setOnboardingCompleted(false);
          setActiveScreen('gender-select');
        } else if (gender === 'male') {
          // Male user (is_it is always true)
          if (status === 0) {
            // Status 0 -> fetch profile and determine which step to resume
            console.log('→ Male, status 0, fetching profile to determine step');
            setOnboardingCompleted(false);
            
            try {
              // Fetch full profile to determine which step to resume
              const profile = await api.getMyProfile();
              
              // Determine which onboarding step based on missing data
              let resumeStep = 0;
              
              if (!profile.photos || profile.photos.length === 0) {
                resumeStep = 0; // Photos step
                console.log('→ Resuming at step 0 (photos missing)');
              } else if (!profile.birthdate || !profile.bio || !profile.name) {
                resumeStep = 1; // Basic info step
                console.log('→ Resuming at step 1 (basic info missing)');
              } else if (!profile.specializations || profile.specializations.length === 0) {
                resumeStep = 2; // Specialization step (males are always IT)
                console.log('→ Resuming at step 2 (specialization missing)');
              } else if (!profile.lifestyleTagId) {
                resumeStep = 3; // Lifestyle step (males are always IT, so step 3)
                console.log('→ Resuming at step 3 (lifestyle missing)');
              } else {
                resumeStep = 0; // Fallback to start
                console.log('→ Status 0 but all data present, starting from step 0');
              }
              
              // Prefill onboarding data from existing profile
              updateOnboardingData({
                gender: profile.gender,
                photos: profile.photos || [],
                name: profile.name,
                birthdate: profile.birthdate,
                bio: profile.bio,
                specializationId: profile.specializations?.[0]?.id,
                techStackIds: profile.tech_stack?.map((t: any) => t.id) || [],
                desiredLifestyleId: profile.lifestyleTagId,
                hobbyIds: profile.hobbyIds || [],
              } as any);
              
              setOnboardingStep(resumeStep);
            } catch (error) {
              console.error('Failed to fetch profile for prefill:', error);
              // Fallback: start from beginning
              setOnboardingStep(0);
            }
            
            setActiveScreen('onboarding');
          } else if (status === 1) {
            // Status 1 -> check if specialization is set before IT Quest
            try {
              const profile = await api.getMyProfile();
              
              if (!profile.specializations || profile.specializations.length === 0) {
                // No specialization -> go to onboarding step 2
                console.log('→ Male, status 1, no specialization - resuming at step 2');
                setOnboardingCompleted(false);
                
                // Prefill existing data
                updateOnboardingData({
                  gender: profile.gender,
                  photos: profile.photos || [],
                  name: profile.name,
                  birthdate: profile.birthdate,
                  bio: profile.bio,
                  techStackIds: profile.tech_stack?.map((t: any) => t.id) || [],
                  desiredLifestyleId: profile.lifestyleTagId,
                  hobbyIds: profile.hobbyIds || [],
                } as any);
                
                setOnboardingStep(2); // Specialization step
                setActiveScreen('onboarding');
              } else {
                // Has specialization -> IT Quest test
                console.log('→ Male, status 1, has specialization - showing IT Quest');
                setOnboardingCompleted(false);
                setActiveScreen('it-quest');
              }
            } catch (error) {
              console.error('Failed to check specialization:', error);
              // Fallback: try IT Quest anyway
              setOnboardingCompleted(false);
              setActiveScreen('it-quest');
            }
          } else {
            // Status >= 2 -> completed
            console.log('→ Male, status >= 2, showing feed');
            setOnboardingCompleted(true);
            setActiveScreen('feed');
          }
        } else if (gender === 'female') {
          // Female user - check is_it status
          if (isIT === undefined || isIT === null) {
            // IT status not chosen yet -> show IT choice
            console.log('→ Female, is_it not set, showing IT choice');
            setOnboardingCompleted(false);
            setActiveScreen('it-choice');
          } else if (status === 0) {
            // Status 0 -> fetch profile and determine which step to resume
            console.log('→ Female, status 0, fetching profile to determine step');
            setOnboardingCompleted(false);
            
            try {
              // Fetch full profile to determine which step to resume
              const profile = await api.getMyProfile();
              
              // Determine which onboarding step based on missing data
              let resumeStep = 0;
              
              if (!profile.photos || profile.photos.length === 0) {
                resumeStep = 0; // Photos step
                console.log('→ Resuming at step 0 (photos missing)');
              } else if (!profile.birthdate || !profile.bio || !profile.name) {
                resumeStep = 1; // Basic info step
                console.log('→ Resuming at step 1 (basic info missing)');
              } else if (isIT === true && (!profile.specializations || profile.specializations.length === 0)) {
                // Specialization step - only for IT females
                resumeStep = 2;
                console.log('→ Resuming at step 2 (specialization missing, is_it=true)');
              } else if (!profile.lifestyleTagId) {
                // Lifestyle step - for both IT and non-IT females
                // For non-IT: step 3 (OnboardingScreen skips step 2 for non-IT)
                // For IT: step 3 (after specialization)
                resumeStep = 3;
                console.log(`→ Resuming at step 3 (lifestyle missing, is_it=${isIT})`);
              } else {
                resumeStep = 0; // Fallback to start
                console.log('→ Status 0 but all data present, starting from step 0');
              }
              
              // Prefill onboarding data from existing profile
              updateOnboardingData({
                gender: profile.gender,
                photos: profile.photos || [],
                name: profile.name,
                birthdate: profile.birthdate,
                bio: profile.bio,
                specializationId: profile.specializations?.[0]?.id,
                techStackIds: profile.tech_stack?.map((t: any) => t.id) || [],
                desiredLifestyleId: profile.lifestyleTagId,
                hobbyIds: profile.hobbyIds || [],
              } as any);
              
              setOnboardingStep(resumeStep);
            } catch (error) {
              console.error('Failed to fetch profile for prefill:', error);
              // Fallback: start from beginning
              setOnboardingStep(0);
            }
            
            setActiveScreen('onboarding');
          } else if (isIT === true && status === 1) {
            // IT female with status 1 -> check if specialization is set
            try {
              const profile = await api.getMyProfile();
              
              if (!profile.specializations || profile.specializations.length === 0) {
                // No specialization -> go to onboarding step 2
                console.log('→ Female IT, status 1, no specialization - resuming at step 2');
                setOnboardingCompleted(false);
                
                // Prefill existing data
                updateOnboardingData({
                  gender: profile.gender,
                  photos: profile.photos || [],
                  name: profile.name,
                  birthdate: profile.birthdate,
                  bio: profile.bio,
                  techStackIds: profile.tech_stack?.map((t: any) => t.id) || [],
                  desiredLifestyleId: profile.lifestyleTagId,
                  hobbyIds: profile.hobbyIds || [],
                } as any);
                
                setOnboardingStep(2); // Specialization step
                setActiveScreen('onboarding');
              } else {
                // Has specialization -> IT Quest test
                console.log('→ Female IT, status 1, has specialization - showing IT Quest');
                setOnboardingCompleted(false);
                setActiveScreen('it-quest');
              }
            } catch (error) {
              console.error('Failed to check specialization:', error);
              // Fallback: try IT Quest anyway
              setOnboardingCompleted(false);
              setActiveScreen('it-quest');
            }
          } else if (status !== undefined && status >= 1) {
            // Status >= 1 -> check completion based on is_it
            // For IT users: need status 2 (verified)
            // For non-IT users: status 1 is enough (profile_complete)
            const completed = isIT ? status >= 2 : status >= 1;
            if (completed) {
              console.log(`→ Female ${isIT ? 'IT' : 'non-IT'}, registration complete, showing feed`);
              setOnboardingCompleted(true);
              setActiveScreen('feed');
            } else {
              console.log(`→ Female ${isIT ? 'IT' : 'non-IT'}, incomplete registration`);
              setOnboardingCompleted(false);
              setActiveScreen('onboarding');
            }
          }
        }
      } else {
        // No user data - new user (dev mode without telegram)
        console.log('→ No user data, showing gender select');
        setOnboardingCompleted(false);
        setActiveScreen('gender-select');
      }
      
      // Update Telegram user info
      const telegramUser = TelegramUtils.getUserInfo();
      if (telegramUser) {
        setTelegramUser(telegramUser);
      }
      setTelegramReady(true);

      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Less retries for faster feedback
    ...options,
  });
}

// Profile Queries
export function useProfile(id: string, options?: UseQueryOptions<FullProfile>) {
  return useQuery({
    queryKey: queryKeys.profile(id),
    queryFn: () => api.getProfile(id),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

export function useMyProfile(options?: UseQueryOptions<FullProfile>) {
  return useQuery({
    queryKey: queryKeys.myProfile,
    queryFn: () => api.getMyProfile(),
    staleTime: 1 * 60 * 1000, // 1 minute
    ...options,
  });
}

// Feed Queries
export function useFeedProfiles(options?: UseQueryOptions<ProfileSummary[]>) {
  const { setFeedProfiles, setFeedHasMore, setFeedLoading } = useAppStore();

  return useQuery({
    queryKey: queryKeys.feedProfiles,
    queryFn: async () => {
      setFeedLoading(true);
      try {
        const cards = await api.getDiscoveryCards(10);
        const profiles = cards.map(card => mapCardToProfileSummary(card));
        setFeedProfiles(profiles);
        setFeedHasMore(cards.length >= 10); // If we got full page, there might be more
        return profiles;
      } finally {
        setFeedLoading(false);
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    // refetchOnMount defaults to true, so data will refetch when stale or invalidated
    ...options,
  });
}

// Matches Queries
export function useMatches(options?: UseQueryOptions<Match[]>) {
  const { user, setMatches, setMatchesLoading } = useAppStore();

  return useQuery({
    queryKey: queryKeys.matches,
    queryFn: async () => {
      setMatchesLoading(true);
      try {
        const matchResponses = await api.getMatches();
        const currentUserId = user?.id || '0';
        const matches = matchResponses.map(mr => mapMatchResponseToMatch(mr, currentUserId));
        setMatches(matches);
        return matches;
      } finally {
        setMatchesLoading(false);
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

export function useMatch(matchId: string, options?: UseQueryOptions<Match>) {
  return useQuery({
    queryKey: queryKeys.match(matchId),
    queryFn: () => api.getMatch(matchId),
    staleTime: 1 * 60 * 1000, // 1 minute
    enabled: !!matchId,
    ...options,
  });
}

// Messages Queries with Pagination
export function useMessages(matchId: string | number, options?: any) {
  return useInfiniteQuery({
    queryKey: queryKeys.messages(matchId.toString()),
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const messageResponses = await api.getMatchMessages(Number(matchId), 50, pageParam);
      const messages = messageResponses.map(mr => mapMessageResponseToMessage(mr, matchId.toString()));
      return messages;
    },
    getNextPageParam: (lastPage: Message[], allPages: Message[][]) => {
      // If last page has messages, there might be more
      // Next offset = total messages fetched so far
      if (lastPage.length === 50) {
        return allPages.flat().length;
      }
      return undefined; // No more pages
    },
    initialPageParam: 0,
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!matchId,
    refetchInterval: 10 * 1000, // Poll every 10 seconds for new messages
    ...options,
  });
}

// Likes Query (Premium feature)
export function useLikes(options?: UseQueryOptions<any>) {
  return useQuery({
    queryKey: queryKeys.likes,
    queryFn: () => api.getLikes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// Notification Counters Query
export function useNotificationCounters(options?: UseQueryOptions<NotificationCounters>) {
  return useQuery({
    queryKey: queryKeys.notificationCounters,
    queryFn: () => api.getNotificationCounters(),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
    ...options,
  });
}

// Subscription Status Query
export function useSubscriptionStatus(options?: UseQueryOptions<SubscriptionStatusResponse>) {
  return useQuery({
    queryKey: ['subscriptions', 'status'],
    queryFn: () => api.getSubscriptionStatus(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

// My Likes Query (outgoing likes)
export function useMyLikes(options?: UseQueryOptions<MyLikesResponse>) {
  return useQuery({
    queryKey: ['my-likes'],
    queryFn: () => api.getMyLikes(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

// Mutations
export function useCreateProfile(options?: UseMutationOptions<FullProfile, Error, Partial<OnboardingData>>) {
  const queryClient = useQueryClient();
  const { setUser, setAuthenticated, setOnboardingCompleted } = useAppStore();

  return useMutation({
    mutationFn: (data: Partial<OnboardingData>) => api.createProfile(data),
    onSuccess: (profile) => {
      // Update store
      setUser(profile);
      setAuthenticated(true);
      setOnboardingCompleted(true);
      
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });
      queryClient.invalidateQueries({ queryKey: queryKeys.bootstrap });
      
      // Haptic feedback
      TelegramUtils.haptic.success();
    },
    onError: () => {
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useUpdateProfile(options?: UseMutationOptions<FullProfile, Error, Partial<FullProfile>>) {
  const queryClient = useQueryClient();
  const { updateUser } = useAppStore();

  return useMutation({
    mutationFn: (data: Partial<FullProfile>) => api.updateProfile(data),
    onSuccess: (profile) => {
      // Update store
      updateUser(profile);
      
      // Update cache
      queryClient.setQueryData(queryKeys.myProfile, profile);
      queryClient.invalidateQueries({ queryKey: queryKeys.profile(profile.id) });
      
      // Haptic feedback
      TelegramUtils.haptic.success();
    },
    onError: () => {
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useUploadPhoto(options?: UseMutationOptions<{ url: string }, Error, File>) {
  return useMutation({
    mutationFn: (file: File) => api.uploadPhoto(file),
    onSuccess: () => {
      TelegramUtils.haptic.success();
    },
    onError: () => {
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useSwipeProfile(options?: UseMutationOptions<SwipeResponse, Error, SwipeRequest>) {
  const queryClient = useQueryClient();
  const { removeProfileFromFeed, showToast, setModal } = useAppStore();

  return useMutation({
    mutationFn: async (request: SwipeRequest) => {
      // Convert to API format
      const action = request.direction === 'super_like' ? 'superlike' : request.direction;
      const response = await api.swipeUser({
        target_user_id: Number(request.targetId),
        action: action as 'like' | 'pass' | 'superlike',
      });

      // Convert back to app format
      return {
        matched: response.is_match,
        matchId: response.is_match ? request.targetId : undefined,
        message: response.is_match ? "It's a match! 🎉" : undefined,
      };
    },
    onSuccess: async (response, request) => {
      // Remove profile from feed
      removeProfileFromFeed(request.targetId);
      
      // If it's a match, show celebration
      if (response.matched) {
        // Refetch matches to get the new match
        await queryClient.invalidateQueries({ queryKey: queryKeys.matches });
        
        // Wait a bit for the query to refetch
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get the updated matches from cache
        const matchesData = queryClient.getQueryData<any>(queryKeys.matches);
        
        // Find the match with this user
        let matchId: string | undefined;
        if (matchesData && Array.isArray(matchesData)) {
          const match = matchesData.find((m: any) => 
            m.users?.some((u: any) => u.id === request.targetId)
          );
          matchId = match?.id;
        }
        
        // Show match celebration modal with both userId and matchId
        setModal('match-celebration', { 
          userId: request.targetId,
          matchId: matchId 
        });
        
        // Haptic feedback for match
        TelegramUtils.haptic.success();
      } else {
        // Regular haptic feedback for swipe
        if (request.direction === 'like') {
          TelegramUtils.haptic.light();
        } else if (request.direction === 'super_like') {
          TelegramUtils.haptic.medium();
        } else {
          TelegramUtils.haptic.selection();
        }
      }
      
      // Note: Cards are only reloaded when returning to feed screen, not on each swipe
    },
    onError: (error: any) => {
      // Handle specific errors
      if (error.message?.includes('super like limit')) {
        showToast('Лимит супер-лайков исчерпан. Купите Premium!', 'warning');
      } else if (error.message?.includes('Already swiped')) {
        showToast('Вы уже оценили этот профиль', 'info');
      } else {
        showToast('Не удалось выполнить действие', 'error');
      }
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useSendMessage(options?: UseMutationOptions<Message, Error, { matchId: string; data: MessageCreate; replyTo?: Message | null }, { previousMessages?: any }>) {
  const queryClient = useQueryClient();
  const { updateMatch } = useAppStore();
  const { user } = useAppStore.getState();

  return useMutation<Message, Error, { matchId: string; data: MessageCreate; replyTo?: Message | null }, { previousMessages?: any }>({
    mutationFn: ({ matchId, data }) => api.sendMessageV2(Number(matchId), data),
    
    // Optimistic update - показываем сообщение сразу
    onMutate: async ({ matchId, data, replyTo }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.messages(matchId) });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData(queryKeys.messages(matchId));

      // Optimistically add the new message
      const optimisticMessage: Message = {
        id: `temp-${Date.now()}`,
        matchId,
        senderId: user?.id || '0',
        text: data.content || null,
        timestamp: new Date().toISOString(),
        read: false,
        type: 'text',
        photoUrl: data.photo_url || null,
        replyToMessageId: replyTo?.id || null,
        repliedMessage: replyTo || null,
        deletedAt: null,
        editedAt: null,
        isEdited: false,
      };

      // Update infinite query cache
      queryClient.setQueryData(queryKeys.messages(matchId), (old: any) => {
        if (!old) {
          return {
            pages: [[optimisticMessage]],
            pageParams: [0],
          };
        }

        // Add to the last page
        const newPages = [...old.pages];
        const lastPageIndex = newPages.length - 1;
        newPages[lastPageIndex] = [...newPages[lastPageIndex], optimisticMessage];

        return {
          ...old,
          pages: newPages,
        };
      });

      // Haptic feedback
      TelegramUtils.haptic.light();

      // Return context for rollback
      return { previousMessages };
    },

    // On success, replace optimistic message with real one
    onSuccess: (message, { matchId }, context) => {
      // Update match with last message
      updateMatch(matchId, { lastMessage: message });

      // Replace optimistic message with real one
      queryClient.setQueryData(queryKeys.messages(matchId), (old: any) => {
        if (!old) return old;

        const newPages = old.pages.map((page: Message[]) =>
          page.map((msg: Message) =>
            msg.id.startsWith('temp-') && msg.text === message.text ? message : msg
          )
        );

        return {
          ...old,
          pages: newPages,
        };
      });
    },

    // On error, rollback
    onError: (err, variables, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(
          queryKeys.messages(variables.matchId),
          context.previousMessages
        );
      }
      TelegramUtils.haptic.error();
    },

    ...options,
  });
}

// Messaging 2.0 Mutations
export function useEditMessage(options?: UseMutationOptions<Message, Error, { matchId: string; messageId: number; content: string }>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: ({ messageId, content }) => api.editMessage(messageId, content),
    
    onSuccess: (updated, { matchId }) => {
      // Обновить сообщение в кэше infinite query
      queryClient.setQueryData(queryKeys.messages(matchId), (old: any) => {
        if (!old) return old;
        
        const newPages = old.pages.map((page: Message[]) =>
          page.map((msg: Message) =>
            msg.id === updated.id ? { ...updated, matchId } : msg
          )
        );
        
        return { ...old, pages: newPages };
      });
      
      TelegramUtils.haptic.success();
    },
    
    onError: () => {
      showToast('Не удалось отредактировать сообщение', 'error');
      TelegramUtils.haptic.error();
    },
    
    ...options,
  });
}

export function useDeleteMessage(options?: UseMutationOptions<{ success: boolean; message: string }, Error, { matchId: string; messageId: number }>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: ({ messageId }) => api.deleteMessage(messageId),
    
    onSuccess: (_, { matchId, messageId }) => {
      // Обновить сообщение в кэше как удаленное
      queryClient.setQueryData(queryKeys.messages(matchId), (old: any) => {
        if (!old) return old;
        
        const now = new Date().toISOString();
        const newPages = old.pages.map((page: Message[]) =>
          page.map((msg: Message) =>
            msg.id === messageId.toString() 
              ? { ...msg, text: 'Сообщение удалено', deletedAt: now }
              : msg
          )
        );
        
        return { ...old, pages: newPages };
      });
      
      TelegramUtils.haptic.success();
    },
    
    onError: () => {
      showToast('Не удалось удалить сообщение', 'error');
      TelegramUtils.haptic.error();
    },
    
    ...options,
  });
}

export function useReportMessage(options?: UseMutationOptions<{ success: boolean; message: string; report_id: number }, Error, { messageId: number; reason: string }>) {
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: ({ messageId, reason }) => api.reportMessage(messageId, reason),
    
    onSuccess: (response) => {
      showToast('Жалоба отправлена. Спасибо за заботу о сообществе!', 'success');
      TelegramUtils.haptic.success();
    },
    
    onError: () => {
      showToast('Не удалось отправить жалобу', 'error');
      TelegramUtils.haptic.error();
    },
    
    ...options,
  });
}

export function useUnmatch(options?: UseMutationOptions<void, Error, string>) {
  const queryClient = useQueryClient();
  const { removeMatch, showToast } = useAppStore();

  return useMutation({
    mutationFn: (matchId: string) => api.unmatch(matchId),
    onSuccess: (_, matchId) => {
      // Remove match from store
      removeMatch(matchId);
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: queryKeys.matches });
      queryClient.removeQueries({ queryKey: queryKeys.match(matchId) });
      queryClient.removeQueries({ queryKey: queryKeys.messages(matchId) });
      
      // Show feedback
      showToast('Match removed', 'info');
      TelegramUtils.haptic.light();
    },
    onError: () => {
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useStarMatch(options?: UseMutationOptions<void, Error, { matchId: string; starred: boolean }>) {
  const queryClient = useQueryClient();
  const { updateMatch } = useAppStore();

  return useMutation({
    mutationFn: ({ matchId, starred }) => api.starMatch(matchId, starred),
    onSuccess: (_, { matchId, starred }) => {
      // Update match in store
      updateMatch(matchId, { isStarred: starred });
      
      // Update cache
      queryClient.setQueryData(
        queryKeys.match(matchId),
        (old: Match | undefined) => {
          if (!old) return old;
          return { ...old, isStarred: starred };
        }
      );
      
      // Haptic feedback
      TelegramUtils.haptic.selection();
    },
    onError: () => {
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useReportUser(options?: UseMutationOptions<void, Error, { userId: string; reason: string }>) {
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: ({ userId, reason }) => {
      console.log('📡 API: Calling reportUser', { userId, reason });
      return api.reportUser(userId, reason);
    },
    onSuccess: () => {
      console.log('✅ API: Report successful');
      TelegramUtils.haptic.success();
    },
    onError: (error) => {
      console.error('❌ API: Report failed', error);
      showToast('Failed to report user. Please try again.', 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useBlockUser(options?: UseMutationOptions<void, Error, string>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: (userId: string) => {
      console.log('📡 API: Calling blockUser', { userId });
      return api.blockUser(userId);
    },
    onSuccess: () => {
      console.log('✅ API: Block successful');
      // Invalidate all queries to remove blocked user
      queryClient.invalidateQueries({ queryKey: queryKeys.feedProfiles });
      queryClient.invalidateQueries({ queryKey: queryKeys.matches });
      queryClient.invalidateQueries({ queryKey: ['blocked-users'] });
      
      showToast('User blocked', 'info');
      TelegramUtils.haptic.light();
    },
    onError: (error) => {
      console.error('❌ API: Block failed', error);
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useBlockedUsers(options?: UseQueryOptions<any, Error>) {
  return useQuery({
    queryKey: ['blocked-users'],
    queryFn: () => api.getBlockedUsers(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

export function useUnblockUser(options?: UseMutationOptions<void, Error, string>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: (userId: string) => api.unblockUser(userId),
    onSuccess: () => {
      // Refresh blocked users list
      queryClient.invalidateQueries({ queryKey: ['blocked-users'] });
      
      showToast('User unblocked', 'info');
      TelegramUtils.haptic.light();
    },
    onError: () => {
      showToast('Failed to unblock user. Please try again.', 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

// Premium feature mutations
export function useRewind(options?: UseMutationOptions<ProfileSummary | null, Error, void>) {
  const queryClient = useQueryClient();
  const { addFeedProfiles, showToast } = useAppStore();

  return useMutation({
    mutationFn: () => api.rewind(),
    onSuccess: (profile) => {
      if (profile) {
        // Add profile back to feed
        addFeedProfiles([profile]);
        TelegramUtils.haptic.medium();
      } else {
        showToast('No recent swipes to rewind', 'info');
      }
    },
    onError: () => {
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useBoost(options?: UseMutationOptions<{ expiresAt: string }, Error, void>) {
  return useMutation({
    mutationFn: () => api.boost(),
    onSuccess: () => {
      TelegramUtils.haptic.success();
    },
    onError: () => {
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

// Profile Update Mutations
export function useUpdateBasicInfo(options?: UseMutationOptions<any, Error, any>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: (data: any) => {
      return (api as any).updateBasicInfo(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });
      TelegramUtils.haptic.success();
    },
    onError: () => {
      showToast('Не удалось обновить профиль', 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useUpdateLifestyle(options?: UseMutationOptions<any, Error, number>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: (lifestyleTagId: number) => {
      return (api as any).updateLifestyle(lifestyleTagId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });
      TelegramUtils.haptic.success();
    },
    onError: () => {
      showToast('Не удалось обновить lifestyle', 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useUpdateITInfo(options?: UseMutationOptions<any, Error, any>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: (data: any) => {
      return (api as any).updateITInfo(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });
      TelegramUtils.haptic.success();
    },
    onError: () => {
      showToast('Не удалось обновить информацию', 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useUploadPhotosBulk(options?: UseMutationOptions<any, Error, string[]>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: (photoUrls: string[]) => {
      return (api as any).uploadPhotosBulk(photoUrls);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });
      TelegramUtils.haptic.success();
    },
    onError: () => {
      showToast('Не удалось загрузить фото', 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

// Remove Like Mutation
export function useRemoveLike(options?: UseMutationOptions<any, Error, RemoveLikeRequest>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: (data: RemoveLikeRequest) => api.removeLike(data),
    onSuccess: () => {
      // Invalidate my likes to refresh the list
      queryClient.invalidateQueries(['my-likes']);
      TelegramUtils.haptic.success();
    },
    onError: () => {
      showToast('Не удалось удалить лайк', 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useDeleteAccount(options?: UseMutationOptions<void, Error, void>) {
  const queryClient = useQueryClient();
  const { showToast, setActiveScreen, reset } = useAppStore();

  return useMutation({
    mutationFn: async () => {
      console.log('🔥 useDeleteAccount: mutationFn called');
      console.log('🔥 Step 1: Calling api.deleteAccount() - ДЕЛАЕМ ПЕРВЫМ ДЕЛОМ');
      
      // FIRST: Delete the account via API (before clearing any data)
      try {
        const result = await api.deleteAccount();
        console.log('✅ api.deleteAccount() completed successfully:', result);
        
        // Wait to ensure the backend processed the request
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        console.log('✅ Step 2: Account deletion confirmed by server');
        return result;
      } catch (error) {
        console.error('❌ Failed to delete account from server:', error);
        throw error; // Re-throw to trigger onError
      }
    },
    onSuccess: () => {
      console.log('✅ useDeleteAccount: onSuccess - account deleted from server');
      console.log('🗑️ Step 3: Now clearing all local data...');
      
      // Clear all caches
      console.log('🗑️ Clearing React Query cache');
      queryClient.clear();
      
      // Clear all storage
      console.log('🗑️ Clearing localStorage');
      localStorage.clear();
      console.log('🗑️ Clearing sessionStorage');
      sessionStorage.clear();
      
      // Clear IndexedDB (used by zustand persist)
      if (window.indexedDB) {
        window.indexedDB.databases().then(databases => {
          databases.forEach(db => {
            if (db.name) {
              console.log('🗑️ Deleting IndexedDB:', db.name);
              window.indexedDB.deleteDatabase(db.name);
            }
          });
        }).catch(console.error);
      }
      
      // Clear Telegram CloudStorage
      console.log('🗑️ Clearing Telegram CloudStorage');
      TelegramUtils.storage.clear().catch(console.error);
      
      // Reset store
      console.log('🔄 Resetting store');
      reset();
      
      // Force reload immediately to clear everything
      console.log('🔄 Step 4: Reloading page now');
      window.location.reload();
    },
    onError: (error: any) => {
      console.error('❌ useDeleteAccount: onError called', error);
      showToast(`Не удалось удалить профиль: ${error.message}`, 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

// Filters hooks
export function useBasicFilters(options?: UseQueryOptions<BasicFiltersResponse>) {
  return useQuery({
    queryKey: ['filters', 'basic'],
    queryFn: () => api.getBasicFilters(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useUpdateBasicFilters(options?: UseMutationOptions<void, Error, Partial<BasicFilters>>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: (filters: Partial<BasicFilters>) => api.updateBasicFilters(filters),
    onSuccess: () => {
      // Invalidate filters and feed
      queryClient.invalidateQueries(['filters', 'basic']);
      queryClient.invalidateQueries(queryKeys.feedProfiles);
      
      TelegramUtils.haptic.success();
    },
    onError: () => {
      showToast('Не удалось обновить фильтры', 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}

export function useResetBasicFilters(options?: UseMutationOptions<void, Error, void>) {
  const queryClient = useQueryClient();
  const { showToast } = useAppStore();

  return useMutation({
    mutationFn: () => api.resetBasicFilters(),
    onSuccess: () => {
      // Invalidate filters and feed
      queryClient.invalidateQueries(['filters', 'basic']);
      queryClient.invalidateQueries(queryKeys.feedProfiles);
      
      TelegramUtils.haptic.success();
    },
    onError: () => {
      showToast('Не удалось сбросить фильтры', 'error');
      TelegramUtils.haptic.error();
    },
    ...options,
  });
}
